import * as React from "react";
import { tv, VariantProps } from "tailwind-variants";
import { cn } from "@repo/utils/cn";
import { ComponentProps } from "react";
import { motion, AnimatePresence } from "motion/react";
import { ChevronIcon } from "./ChevronIcon";
import { Button, ButtonProps } from "./Button";
import { Title, TitleProps } from "./Title";
import Image from "next/image";
import { parseAiAlt } from "@repo/ai-disclaimer";
import { AiDisclaimerForAlt } from "./AiDisclaimer";
import { sectionWrapperFull as sectionWrapper } from "./sectionWrappers";

const articleContainer = tv({
  base: cn(
    "uipm:flex uipm:flex-col",
    "uipm:gap-[var(--spacing-primitives-32)]",
    "uipm:md:gap-[var(--spacing-primitives-48)]",
    "uipm:lg:flex-row uipm:lg:items-stretch uipm:lg:gap-[var(--container-gutter)]",
  ),
});

const contentSection = tv({
  base: cn(
    "uipm:flex uipm:flex-col uipm:justify-center uipm:items-start",
    "uipm:gap-[var(--margin-margin-default)]",
    "uipm:w-full",
    "uipm:lg:flex-1 uipm:lg:basis-0 uipm:lg:w-auto",
  ),
});

const navigationContainer = tv({
  base: cn(
    "uipm:flex uipm:justify-start uipm:items-center",
    "uipm:gap-[var(--container-gutter)]",
    "uipm:md:gap-[var(--container-gutter)]",
    "uipm:lg:px-[16px]",
  ),
});

const navigationButton = tv({
  base: cn(
    "uipm:flex uipm:items-center uipm:justify-center",
    "uipm:w-[var(--spacing-primitives-48)] uipm:h-[var(--spacing-primitives-48)]",
    "uipm:p-[12px] uipm:rounded-full",
    "uipm:bg-[var(--surface-surface-secondary)]",
    "uipm:transition-opacity uipm:duration-200",
    "uipm:disabled:opacity-50 uipm:disabled:cursor-not-allowed",
    "uipm:hover:opacity-80",
  ),
});

const categoryText = tv({
  base: cn(
    "uipm:text-[var(--typography-colors-text-faded-dark)]",
    "uipm:fz-[var(--typography-sizes-body-medium)]",
    "uipm:font-medium",
    "uipm:leading-[var(--typography-line-heights-body-medium)]",
  ),
});

const buttonsContainer = tv({
  base: cn(
    "uipm:pt-[var(--spacing-primitives-24)]",
    "uipm:flex uipm:flex-col uipm:justify-start uipm:items-center",
    "uipm:gap-[var(--spacing-primitives-16)]",
    "uipm:w-full",
    "uipm:md:flex-row",
    "uipm:lg:justify-between uipm:lg:items-center",
  ),
});

const buttonsGroup = tv({
  base: cn(
    "uipm:flex uipm:justify-start uipm:items-center",
    "uipm:gap-[var(--spacing-primitives-16)]",
    "uipm:w-full",
    "uipm:lg:justify-start",
  ),
});

const imageContainer = tv({
  base: cn(
    "uipm:w-full",
    "uipm:flex uipm:justify-center uipm:items-center",
    "uipm:lg:flex-1 uipm:lg:basis-0 uipm:lg:w-auto",
  ),
});

export interface ArticleHighlightItem {
  category: string | undefined;
  title: TitleProps | undefined;
  image:
    | {
        src: string;
        alt: string;
        width: number;
        height: number;
        quality: number;
        aspectRatio: number;
      }
    | undefined;
  buttons: ButtonProps[] | undefined;
}

export interface ArticleHighlightProps
  extends Omit<ComponentProps<"section">, "title"> {
  backgroundColor?: "none" | "light" | "accent" | "accent-light";
  topPadding?: "none" | "default" | "large";
  bottomPadding?: "none" | "default";
  layout?: "default" | "reversed";
  items?: ArticleHighlightItem[];
}

export const ArticleHighlight: React.FC<ArticleHighlightProps> = ({
  backgroundColor = "none",
  topPadding = "none",
  bottomPadding = "none",
  layout = "default",
  items = [],
  className,
  id,
  ...props
}) => {
  const [selectedIndex, setSelectedIndex] = React.useState(0);

  const scrollPrev = React.useCallback(() => {
    setSelectedIndex((current) => Math.max(0, current - 1));
  }, []);

  const scrollNext = React.useCallback(() => {
    setSelectedIndex((current) => Math.min(items.length - 1, current + 1));
  }, [items.length]);

  const canScrollPrev = selectedIndex > 0;
  const canScrollNext = selectedIndex < items.length - 1;

  const hasItems = items && items.length > 0;
  const hasMultipleItems = items && items.length > 1;
  const currentItem = hasItems ? items[selectedIndex] : null;

  const isReversed = layout === "reversed";

  if (!hasItems) return null;

  return (
    <section
      id={id}
      className={sectionWrapper({ backgroundColor, topPadding, bottomPadding })}
      {...props}
    >
      <div className="uipm:max-w-[1440px] uipm:mx-auto uipm:px-[var(--container-margin)]">
        <div
          className={cn(
            articleContainer(),
            isReversed && "uipm:flex-col uipm:lg:flex-row-reverse",
            className,
          )}
        >
          <div
            className={cn(
              contentSection(),
              !isReversed
                ? "uipm:lg:pl-[var(--container-card-large-padding)]"
                : "uipm:lg:pr-[var(--container-card-large-padding)]",
            )}
          >
            {/* Mobile/Tablet Navigation - Above content */}
            {hasMultipleItems && (
              <div className="uipm:lg:hidden">
                <div className={navigationContainer()}>
                  <button
                    className={navigationButton()}
                    onClick={scrollPrev}
                    disabled={!canScrollPrev}
                    aria-label="Previous"
                  >
                    <ChevronIcon
                      className="uipm:w-6 uipm:h-6 uipm:rotate-180"
                      color="var(--color-color-accent)"
                    />
                  </button>
                  <button
                    className={navigationButton()}
                    onClick={scrollNext}
                    disabled={!canScrollNext}
                    aria-label="Next"
                  >
                    <ChevronIcon
                      className="uipm:w-6 uipm:h-6"
                      color="var(--color-color-accent)"
                    />
                  </button>
                </div>
              </div>
            )}

            {/* Content with fade animation */}
            <div className="uipm:relative uipm:w-full">
              <AnimatePresence mode="wait">
                {currentItem && (
                  <motion.div
                    key={selectedIndex}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="uipm:flex uipm:flex-col uipm:justify-start uipm:items-start uipm:gap-[var(--spacing-primitives-8)]"
                  >
                    {/* Category */}
                    {currentItem.category && (
                      <div className={categoryText()}>
                        {currentItem.category}
                      </div>
                    )}

                    {/* Title */}
                    {currentItem.title && (
                      <Title {...currentItem.title} level="h4" fontSize="h4" />
                    )}

                    <div className={buttonsContainer()}>
                      {currentItem.buttons &&
                        currentItem.buttons.length > 0 && (
                          <div className={buttonsGroup()}>
                            {currentItem.buttons.map((button, buttonIndex) => (
                              <Button
                                {...button}
                                key={buttonIndex}
                                className="uipm:flex-[1_0_0] uipm:md:flex-initial"
                              />
                            ))}
                          </div>
                        )}

                      {hasMultipleItems && (
                        <div className="uipm:hidden uipm:lg:flex">
                          <div className={navigationContainer()}>
                            <button
                              className={navigationButton()}
                              onClick={scrollPrev}
                              disabled={!canScrollPrev}
                              aria-label="Previous"
                            >
                              <ChevronIcon
                                className="uipm:w-6 uipm:h-6 uipm:rotate-180"
                                color="var(--color-color-accent)"
                              />
                            </button>
                            <button
                              className={navigationButton()}
                              onClick={scrollNext}
                              disabled={!canScrollNext}
                              aria-label="Next"
                            >
                              <ChevronIcon
                                className="uipm:w-6 uipm:h-6"
                                color="var(--color-color-accent)"
                              />
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>

          {/* Image Section */}
          {currentItem?.image && (
            <div className={imageContainer()}>
              <AnimatePresence mode="wait">
                <motion.div
                  key={selectedIndex}
                  className="uipm:relative uipm:w-full uipm:max-w-[420px] uipm:h-[380px] uipm:md:h-[420px] uipm:lg:h-[420px] uipm:mx-auto"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <Image
                    src={currentItem.image.src}
                    alt={parseAiAlt(currentItem.image.alt).cleanAlt}
                    fill
                    sizes="(max-width: 768px) 100vw, (max-width: 1024px) 420px, 420px"
                    className="uipm:object-cover uipm:rounded-[16px]"
                    priority={selectedIndex === 0}
                  />
                  <AiDisclaimerForAlt
                    alt={currentItem.image.alt}
                    src={currentItem.image.src}
                  />
                </motion.div>
              </AnimatePresence>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export type ArticleHighlightVariants = VariantProps<typeof sectionWrapper>;
